import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, Target, Flame, BarChart3, CopyPlus, Bell, Play } from "lucide-react";
import { toast } from "sonner";
import { API, axios, DAYS, SLOTS, getWeekKey, getMonday, addDays, weekRangeLabel, answerStatusOf } from "../lib/planner";
import { ProgressRing } from "../components/planner/ProgressRing";
import { EncouragementBanner } from "../components/planner/EncouragementBanner";
import { TargetDialog } from "../components/planner/TargetDialog";
import { EntryDialog } from "../components/planner/EntryDialog";
import { DayCard } from "../components/planner/DayCard";
import { InsightsSheet } from "../components/planner/InsightsSheet";
import { AppNav } from "../components/planner/AppNav";
import { FocusMode } from "../components/planner/FocusMode";
import { Button } from "../components/ui/button";

export default function Planner() {
  const [monday, setMonday] = useState(getMonday(new Date()));
  const weekKey = useMemo(() => getWeekKey(monday), [monday]);

  const [entries, setEntries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);

  const [targetOpen, setTargetOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [context, setContext] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem("sanctuary_board_filter") || null);
  const [focusEntry, setFocusEntry] = useState(null);

  useEffect(() => {
    if (statusFilter) localStorage.setItem("sanctuary_board_filter", statusFilter);
    else localStorage.removeItem("sanctuary_board_filter");
  }, [statusFilter]);

  const [encouragement, setEncouragement] = useState("");
  const [encLoading, setEncLoading] = useState(false);
  const prevMet = useRef(false);
  const firedReminders = useRef(new Set());

  const loadStats = useCallback(async () => {
    const res = await axios.get(`${API}/stats`);
    setStats(res.data);
  }, []);

  const loadEntries = useCallback(async () => {
    const res = await axios.get(`${API}/entries`, { params: { week_key: weekKey } });
    setEntries(res.data);
  }, [weekKey]);

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setSettings(r.data));
  }, []);

  const completed = useMemo(() => entries.filter((e) => e.completed), [entries]);
  const totalMinutes = useMemo(() => completed.reduce((s, e) => s + (e.duration_minutes || 0), 0), [completed]);

  const percent = useMemo(() => {
    if (!settings || !settings.target_value) return 0;
    const current = settings.target_type === "minutes" ? totalMinutes : completed.length;
    return Math.min(100, Math.round((current / settings.target_value) * 100));
  }, [settings, totalMinutes, completed.length]);

  const targetMet = percent >= 100;

  const fetchEncouragement = useCallback(async (met) => {
    if (!settings) return;
    setEncLoading(true);
    try {
      const res = await axios.post(`${API}/ai/encouragement`, {
        percent,
        target_type: settings.target_type,
        target_value: settings.target_value,
        target_met: met,
      });
      setEncouragement(res.data.message);
    } catch (e) {
      setEncouragement("Be still, and know. Every moment you turn to prayer is never wasted — keep going.");
    } finally {
      setEncLoading(false);
    }
  }, [settings, percent]);

  useEffect(() => {
    if (settings && !encouragement) fetchEncouragement(false);
  }, [settings]); // eslint-disable-line

  useEffect(() => {
    if (targetMet && !prevMet.current && settings) {
      prevMet.current = true;
      toast.success("Weekly target reached — beautifully done!", { duration: 5000 });
      fetchEncouragement(true);
    }
    if (!targetMet) prevMet.current = false;
  }, [targetMet, settings]); // eslint-disable-line

  useEffect(() => {
    if (!settings?.reminders_enabled) return;
    const reminders = [
      { time: "08:00", label: "Morning prayer", body: "Begin your day in His presence." },
      { time: "13:00", label: "Afternoon prayer", body: "Pause and turn your heart to God." },
      { time: "20:00", label: "Evening prayer", body: "End your day in gratitude and reflection." },
    ];
    const check = () => {
      if (!("Notification" in window) || Notification.permission !== "granted") return;
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const dayKey = now.toDateString();
      reminders.forEach((r) => {
        const key = `${dayKey}-${r.time}`;
        if (r.time === hhmm && !firedReminders.current.has(key)) {
          firedReminders.current.add(key);
          new Notification(`Sanctuary · ${r.label}`, { body: r.body });
        }
      });
    };
    const id = setInterval(check, 30000);
    check();
    return () => clearInterval(id);
  }, [settings?.reminders_enabled]);

  const openNew = (day, slot) => {
    setEditing(null);
    setContext({ day, slot: slot.key, slotLabel: slot.label });
    setEntryOpen(true);
  };

  const openEdit = (entry) => {
    const slot = SLOTS.find((s) => s.key === entry.time_slot);
    setEditing(entry);
    setContext({ day: entry.day, slot: entry.time_slot, slotLabel: slot?.label });
    setEntryOpen(true);
  };

  const saveEntry = async (form) => {
    try {
      if (editing) {
        await axios.put(`${API}/entries/${editing.id}`, form);
        toast.success("Prayer updated");
      } else {
        await axios.post(`${API}/entries`, { ...form, week_key: weekKey, day: context.day, time_slot: context.slot });
        toast.success("Prayer added");
      }
      setEntryOpen(false);
      loadEntries();
      loadStats();
    } catch (e) {
      toast.error("Something went wrong saving");
    }
  };

  const deleteEntry = async (id) => {
    await axios.delete(`${API}/entries/${id}`);
    toast.success("Prayer removed");
    setEntryOpen(false);
    loadEntries();
    loadStats();
  };

  const toggleComplete = async (entry, e) => {
    e.stopPropagation();
    await axios.put(`${API}/entries/${entry.id}`, { completed: !entry.completed });
    loadEntries();
    loadStats();
  };

  const startPrayer = async (entry, e) => {
    if (e) e.stopPropagation();
    await axios.put(`${API}/entries/${entry.id}`, { started_at: new Date().toISOString() });
    toast.success("Prayer started — be present with Him");
    loadEntries();
  };

  const enterFocus = (entry) => {
    setEntryOpen(false);
    setFocusEntry(entry);
  };

  const saveTarget = async (payload) => {
    const res = await axios.put(`${API}/settings`, payload);
    setSettings(res.data);
    setTargetOpen(false);
    toast.success("Target updated");
  };

  const copyLastWeek = async () => {
    const prevKey = getWeekKey(addDays(monday, -7));
    const res = await axios.get(`${API}/entries`, { params: { week_key: prevKey } });
    const prev = res.data;
    if (prev.length === 0) {
      toast.info("No prayers found in last week to copy");
      return;
    }
    await Promise.all(
      prev.map((e) =>
        axios.post(`${API}/entries`, {
          week_key: weekKey,
          day: e.day,
          time_slot: e.time_slot,
          scripture: e.scripture,
          chapter: e.chapter,
          verse: e.verse,
          prayer_point: e.prayer_point,
          time_of_prayer: e.time_of_prayer,
          duration_minutes: e.duration_minutes,
          notes: "",
          summary: "",
          completed: false,
          answered: false,
          answered_note: "",
        })
      )
    );
    toast.success(`Copied ${prev.length} prayer${prev.length > 1 ? "s" : ""} from last week`);
    loadEntries();
  };

  const currentCount = settings?.target_type === "minutes" ? totalMinutes : completed.length;
  const unit = settings?.target_type === "minutes" ? "min" : "sessions";

  const isCurrentWeek = getMonday(new Date()).getTime() === monday.getTime();
  const todayName = DAYS[(new Date().getDay() + 6) % 7];
  const pendingToday = isCurrentWeek
    ? entries.filter((e) => e.day === todayName && !e.completed && !e.started_at)
    : [];

  const applyFilter = (key) => { setStatusFilter(key); setInsightsOpen(false); };

  const answerCounts = useMemo(() => {
    const c = { in_progress: 0, unanswered: 0, answered: 0 };
    entries.forEach((e) => { c[answerStatusOf(e)] = (c[answerStatusOf(e)] || 0) + 1; });
    return c;
  }, [entries]);

  const boardFilters = [
    { key: null, label: "All", count: entries.length },
    { key: "in_progress", label: "In progress", count: answerCounts.in_progress },
    { key: "unanswered", label: "Awaiting", count: answerCounts.unanswered },
    { key: "answered", label: "Answered", count: answerCounts.answered },
  ];

  return (
    <div className="min-h-screen bg-clay-app">
      {/* Header */}
      <header className="sticky top-0 z-30 overflow-hidden border-b border-[#E5E1D8] bg-clay-app/90 backdrop-blur-xl">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(600px circle at 92% -40%, rgba(196,164,124,0.18), transparent 60%)" }}
        />
        <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-5 lg:px-10">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.25em] text-ink-muted">Prayer Planner</p>
            <h1 className="font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Sanctuary</h1>
            <div className="mt-3">
              <AppNav />
            </div>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            {stats?.current_streak > 0 && (
              <div className="flex items-center gap-1.5 rounded-full bg-clay-soft px-3 py-1.5" data-testid="header-streak">
                <Flame className="h-4 w-4 text-[#C4A47C]" />
                <span className="font-sans text-sm font-medium text-ink">{stats.current_streak}</span>
              </div>
            )}
            <button
              onClick={() => setInsightsOpen(true)}
              className="flex items-center gap-1.5 rounded-full border border-[#E5E1D8] bg-white px-3 py-2 text-sm text-ink-soft transition-colors duration-300 hover:bg-clay-panel"
              data-testid="open-insights-btn"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Insights</span>
            </button>
            <div className="hidden text-right sm:block">
              <p className="font-sans text-xs uppercase tracking-[0.1em] text-ink-muted">This week</p>
              <p className="font-serif text-lg text-ink">
                {currentCount} / {settings?.target_value ?? "—"} {unit}
              </p>
            </div>
            <ProgressRing percent={percent} testid="progress-ring" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 lg:px-10">
        {/* Week nav + target */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMonday(addDays(monday, -7))}
              className="rounded-full border border-[#E5E1D8] bg-white p-2 text-ink-soft transition-colors duration-300 hover:bg-clay-panel"
              data-testid="prev-week-btn"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="min-w-[190px] text-center">
              <p className="font-serif text-xl text-ink" data-testid="week-label">{weekRangeLabel(monday)}</p>
            </div>
            <button
              onClick={() => setMonday(addDays(monday, 7))}
              className="rounded-full border border-[#E5E1D8] bg-white p-2 text-ink-soft transition-colors duration-300 hover:bg-clay-panel"
              data-testid="next-week-btn"
              aria-label="Next week"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <Button
              variant="ghost"
              onClick={() => setMonday(getMonday(new Date()))}
              className="rounded-full text-sm text-ink-soft"
              data-testid="today-btn"
            >
              Today
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={copyLastWeek}
              className="rounded-full text-sm text-ink-soft hover:bg-clay-panel"
              data-testid="copy-last-week-btn"
            >
              <CopyPlus className="mr-2 h-4 w-4" /> Copy last week
            </Button>
            <Button
              onClick={() => setTargetOpen(true)}
              className="rounded-full bg-brand text-white hover:bg-brand-hover"
              data-testid="set-target-btn"
            >
              <Target className="mr-2 h-4 w-4" /> Set target
            </Button>
          </div>
        </div>

        {/* Encouragement */}
        <div className="mb-8">
          <EncouragementBanner
            message={encouragement}
            loading={encLoading}
            targetMet={targetMet}
            onRefresh={() => fetchEncouragement(targetMet)}
          />
        </div>

        {/* Polite nudge for today's not-yet-started prayers */}
        {pendingToday.length > 0 && (
          <div
            className="mb-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand/25 bg-[#F2F5EE] px-6 py-4"
            data-testid="today-nudge-banner"
          >
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-brand" />
              <p className="font-serif text-lg italic text-ink">
                You have {pendingToday.length} prayer{pendingToday.length > 1 ? "s" : ""} set for today, still waiting — a quiet moment with God awaits. Will you begin?
              </p>
            </div>
            <Button
              onClick={(e) => startPrayer(pendingToday[0], e)}
              className="rounded-full bg-brand text-white hover:bg-brand-hover"
              data-testid="nudge-start-btn"
            >
              <Play className="mr-2 h-4 w-4" /> Begin now
            </Button>
          </div>
        )}

        {/* Board status filter row */}
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="board-filter-bar">
          <span className="mr-1 font-sans text-xs uppercase tracking-[0.1em] text-ink-muted">Filter</span>
          {boardFilters.map((f) => {
            const active = statusFilter === f.key;
            return (
              <button
                key={f.key || "all"}
                onClick={() => setStatusFilter(f.key)}
                data-testid={`board-filter-${f.key || "all"}`}
                className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-sans text-sm transition-colors duration-300 ${
                  active
                    ? "border-brand bg-brand text-white"
                    : "border-[#E5E1D8] bg-white text-ink-soft hover:bg-clay-panel"
                }`}
              >
                {f.label}
                <span className={`rounded-full px-1.5 text-xs ${active ? "bg-white/25 text-white" : "bg-clay-soft text-ink-muted"}`}>
                  {f.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Week grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {DAYS.map((day, i) => {
            const date = addDays(monday, i);
            const dayEntries = entries.filter(
              (e) => e.day === day && (!statusFilter || answerStatusOf(e) === statusFilter)
            );
            const isToday = getMonday(new Date()).getTime() === monday.getTime() &&
              new Date().getDay() === (i + 1) % 7;
            return (
              <DayCard
                key={day}
                day={day}
                date={date}
                isToday={isToday}
                entries={dayEntries}
                index={i}
                onAdd={openNew}
                onEdit={openEdit}
                onToggle={toggleComplete}
                onStart={startPrayer}
              />
            );
          })}
        </div>
      </main>

      <TargetDialog open={targetOpen} onOpenChange={setTargetOpen} settings={settings} onSave={saveTarget} />
      <EntryDialog
        open={entryOpen}
        onOpenChange={setEntryOpen}
        context={context}
        entry={editing}
        onSave={saveEntry}
        onDelete={deleteEntry}
        onPrayNow={enterFocus}
      />
      <InsightsSheet
        open={insightsOpen}
        onOpenChange={setInsightsOpen}
        entries={entries}
        settings={settings}
        stats={stats}
        weekKey={weekKey}
        onFilterStatus={applyFilter}
      />

      {focusEntry && (
        <FocusMode entry={focusEntry} onClose={() => setFocusEntry(null)} onSaved={() => { loadEntries(); loadStats(); }} />
      )}
    </div>
  );
}
